import React, { useState } from 'react'
import {
  Box,
  Button,
  Chip,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { styled, useTheme } from '@mui/material/styles'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import EditIcon from '@mui/icons-material/Edit'
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined'
import { useApplicationSuiteContext } from '../../../contexts/ApplicationSuiteProvider'
import { usePatternContext } from '../contexts/PatternContext'
import { PatternCopilotProvider } from '../copilot'
import CompositeEditor from './CompositeEditor'
import IndicatorSidePanel from './IndicatorSidePanel'

// Styled components — mirrors the PatternDetailView layout language
const DetailContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  backgroundColor: theme.palette.background.custom1,
  color: theme.palette.text.primary,
}))

const MainEditorArea = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  height: '100%',
  overflow: 'hidden',
  minWidth: 0,
}))

const EditorHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1.5, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.custom2,
  gap: theme.spacing(1.5),
  flexShrink: 0,
}))

// Mirrors PatternDetailView's ControlsBar (the BUY/SELL strip)
const ControlsBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1.5, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.custom1,
  gap: theme.spacing(2),
  flexWrap: 'wrap',
  flexShrink: 0,
}))

const ScrollableContent = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  padding: theme.spacing(2),
}))

/**
 * Full-page indicator editor — the indicators-tab analogue of
 * PatternDetailView, including a right-hand AI Builder dock (the indicators
 * adapter of the copilot) so an indicator idea can be talked through and
 * built in front of the user. Rendered by PatternBuilderApp while
 * editingIndicator is set; closing returns to the indicators list.
 */
const IndicatorDetailView: React.FC = () => (
  <PatternCopilotProvider>
    <IndicatorDetailViewInner />
  </PatternCopilotProvider>
)

const IndicatorDetailViewInner: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { usermeta } = useApplicationSuiteContext()
  const { customIndicators, editingIndicator, setEditingIndicator } = usePatternContext()

  // Admin-only phase 1, mirroring the chatAssistant server 403
  const copilotEnabled = !!usermeta?.isAdmin
  const [mobilePanelOpen, setMobilePanelOpen] = useState(false)

  const editDoc = editingIndicator?.editId
    ? (customIndicators.find((ci) => ci.id === editingIndicator.editId) ?? editingIndicator.doc)
    : undefined

  // New indicators start unnamed, so open straight into name editing.
  const [isEditingName, setIsEditingName] = useState(!editDoc)

  const closeEditor = () => setEditingIndicator(null)

  return (
    <DetailContainer>
      <CompositeEditor key={editDoc?.id ?? 'new'} editDoc={editDoc}>
        {(api) => (
          <>
            <MainEditorArea>
              {/* Header */}
              <EditorHeader>
                <Tooltip title="Back to indicators">
                  <IconButton
                    onClick={() => {
                      // Publish is the only save — leaving a dirty draft loses it
                      if (
                        api.dirty &&
                        !window.confirm('Discard unsaved changes to this indicator?')
                      ) {
                        return
                      }
                      closeEditor()
                    }}
                    size="small"
                  >
                    <ArrowBackIcon />
                  </IconButton>
                </Tooltip>

                {/* Indicator Name */}
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1, minWidth: 0 }}
                >
                  {isEditingName ? (
                    <input
                      autoFocus
                      value={api.name}
                      maxLength={api.maxNameLength}
                      placeholder="Indicator name"
                      onChange={(e) => api.setName(e.target.value)}
                      onBlur={() => setIsEditingName(false)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === 'Escape') setIsEditingName(false)
                      }}
                      style={{
                        border: 'none',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        background: 'transparent',
                        color: theme.palette.text.primary,
                        fontSize: '1.1rem',
                        padding: '4px 8px',
                        width: '300px',
                        maxWidth: '100%',
                        outline: 'none',
                        fontFamily: theme.typography.fontFamily,
                        fontWeight: 600,
                      }}
                    />
                  ) : (
                    <>
                      <Typography
                        variant="h6"
                        color={api.name ? 'text.primary' : 'text.secondary'}
                        sx={{
                          fontWeight: 700,
                          fontSize: '1.1rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {api.name || 'Unnamed Indicator'}
                      </Typography>
                      <Tooltip title="Edit name">
                        <IconButton
                          size="small"
                          onClick={() => setIsEditingName(true)}
                          sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}

                  <Chip
                    label={editDoc ? `v${editDoc.latestVersion + 1} draft` : 'new'}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 24, flexShrink: 0 }}
                  />
                </Box>

                {/* Header Actions */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                  {isMobile && (
                    <Tooltip title="AI & Patterns panel">
                      <IconButton
                        size="small"
                        onClick={() => setMobilePanelOpen(true)}
                        sx={{ color: theme.palette.text.secondary }}
                        data-tour-id="indicator-panel-toggle"
                      >
                        <SmartToyOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    disabled={api.publishDisabled}
                    onClick={api.publish}
                  >
                    {api.publishLabel}
                  </Button>
                </Box>
              </EditorHeader>

              {/* Controls Bar — scale toggle sits where a pattern's BUY/SELL does */}
              <ControlsBar>
                <ToggleButtonGroup
                  color="primary"
                  value={api.scale}
                  exclusive
                  onChange={(_, value) => value && api.setScale(value)}
                  size="small"
                  data-tour-id="indicator-scale-toggle"
                >
                  <ToggleButton value="overlay" sx={{ height: 36, px: 2 }}>
                    OVERLAY
                  </ToggleButton>
                  <ToggleButton value="oscillator" sx={{ height: 36, px: 2 }}>
                    OSCILLATOR
                  </ToggleButton>
                </ToggleButtonGroup>
                <Typography variant="caption" color="text.secondary">
                  Overlay values live on the price scale; oscillators use their own.
                </Typography>
              </ControlsBar>

              {/* Scrollable steps/outputs/validation editor */}
              <ScrollableContent>
                <Box sx={{ maxWidth: 900 }}>{api.body}</Box>
              </ScrollableContent>
            </MainEditorArea>

            {/* Right panel: AI Builder + patterns using this indicator */}
            <IndicatorSidePanel
              key={editDoc?.id ?? 'new'}
              entityId={editDoc?.id ?? null}
              isMobile={isMobile}
              mobileOpen={mobilePanelOpen}
              onMobileClose={() => setMobilePanelOpen(false)}
              copilotEnabled={copilotEnabled}
              name={api.name}
              setName={api.setName}
              steps={api.steps}
              setSteps={api.setSteps}
              outputs={api.outputs}
              setOutputs={api.setOutputs}
              setScale={api.setScale}
            />
          </>
        )}
      </CompositeEditor>
    </DetailContainer>
  )
}

export default IndicatorDetailView
